import { useEffect, useRef, useState } from "react";
import { RefreshCw, X } from "lucide-react";

// Shows a dismissible "Update available" banner ONLY when a genuinely new build
// is deployed (detected by comparing the hashed main.js in /asset-manifest.json
// against the one this session booted with). It never auto-reloads, never runs
// during the Google OAuth flow, and never touches auth caches.
export const UpdateBanner = () => {
  const baseline = useRef(null);
  const dismissed = useRef(null);
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    let stopped = false;

    const readEntry = async () => {
      try {
        const res = await fetch("/asset-manifest.json", { cache: "no-store" });
        if (!res.ok) return null;
        const data = await res.json();
        return (data.files && (data.files["main.js"] || data.files["main.css"])) || null;
      } catch {
        return null;
      }
    };

    const check = async () => {
      // Never interfere with an in-progress sign-in / OAuth callback.
      if (window.location.hash && window.location.hash.includes("session_id=")) return;
      const entry = await readEntry();
      if (!entry || stopped) return;
      if (baseline.current === null) {
        baseline.current = entry; // version this session started on
        return;
      }
      if (entry !== baseline.current && entry !== dismissed.current) {
        setAvailable(true);
      }
    };

    check();
    const onVis = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVis);
    const interval = setInterval(check, 3 * 60 * 1000);

    return () => {
      stopped = true;
      document.removeEventListener("visibilitychange", onVis);
      clearInterval(interval);
    };
  }, []);

  if (!available) return null;

  const refresh = () => window.location.reload();
  const dismiss = () => {
    dismissed.current = baseline.current; // suppress until the NEXT new build
    setAvailable(false);
  };

  return (
    <div
      data-testid="update-available-banner"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] w-[calc(100vw-2rem)] max-w-md"
    >
      <div className="flex items-center gap-3 rounded-xl bg-navy text-bone shadow-2xl ring-1 ring-gold/30 px-4 py-3">
        <RefreshCw className="h-5 w-5 shrink-0 text-gold" />
        <p className="text-sm flex-1 leading-snug">
          A new version is available.
        </p>
        <button
          data-testid="update-refresh-btn"
          onClick={refresh}
          className="shrink-0 rounded-full bg-gold px-4 py-1.5 text-sm font-semibold text-navy transition-colors hover:bg-gold/90"
        >
          Refresh
        </button>
        <button
          data-testid="update-dismiss-btn"
          onClick={dismiss}
          aria-label="Dismiss"
          className="shrink-0 text-bone/60 transition-colors hover:text-bone"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
